<script>
  import { onDestroy } from 'svelte';
  import ErrorBanner from './ErrorBanner.svelte';
  import EmptyState from './EmptyState.svelte';
  import InfoHint from './InfoHint.svelte';
  import Spinner from './Spinner.svelte';
  import { apiKeysApi } from '../stores/api.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { formatDateTime } from '../lib/labels.js';
  import {
    INTEGRATION_PATTERN,
    availablePresets,
    endOfRomeDay,
    groupPermissions,
    integrationLabels,
    requiresIntegration,
    selectsWideRead,
    successorOf,
    validateCreateForm,
  } from '../lib/api-keys.js';
  import { KeyRound, Plus, Copy, Check, Ban, RefreshCw } from '../lib/icons/index.js';

  /**
   * Scoped API keys of ONE company: list, create, replace, revoke.
   *
   * The backend allows all of this to a global administrator signed in through
   * the browser and to nobody else — not the global API key, not a scoped key —
   * and this screen is only reachable from the admin-only company settings, so
   * there is no read-only variant to render.
   *
   * The secret exists in this component for exactly as long as the panel that
   * shows it: it is never put in a store, in storage, or in the list, and the
   * server never returns it again.
   */

  export let companyId = '';

  let catalogue = [];
  let keys = [];
  let loading = true;
  let error = null;

  let showForm = false;
  let successorOfName = '';
  let integrationLocked = false;
  let form = emptyForm();
  let formReason = null;
  let submitting = false;
  let busyKeyId = '';

  let createdKey = '';
  let copyState = '';
  let copyTimer = null;

  $: if (companyId) load(companyId);

  $: catalogueIds = catalogue.map((p) => p.id);
  $: groups = groupPermissions(catalogueIds);
  $: presets = availablePresets(catalogueIds);
  $: knownIntegrations = integrationLabels(keys);
  $: integrationRequired = requiresIntegration(form.permissions);
  $: wideRead = selectsWideRead(form.permissions);

  function emptyForm() {
    return { name: '', integration: '', permissions: [], expiryDay: '' };
  }

  async function load(id) {
    loading = true;
    error = null;
    try {
      const [permissionList, keyList] = await Promise.all([apiKeysApi.permissions(id), apiKeysApi.list(id)]);
      catalogue = permissionList?.permissions || [];
      keys = keyList || [];
    } catch (err) {
      error = err;
    } finally {
      loading = false;
    }
  }

  function openForm() {
    form = emptyForm();
    formReason = null;
    successorOfName = '';
    integrationLocked = false;
    showForm = true;
  }

  function openSuccessor(key) {
    const draft = successorOf(key);
    form = { name: draft.name, integration: draft.integration, permissions: draft.permissions, expiryDay: draft.expiryDay };
    formReason = null;
    successorOfName = key.name;
    // Carried over and not retypable: see successorOf().
    integrationLocked = Boolean(draft.integration);
    showForm = true;
  }

  function closeForm() {
    showForm = false;
    form = emptyForm();
    formReason = null;
  }

  function applyPreset(preset) {
    form.permissions = [...preset.permissions];
    form = form;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const verdict = validateCreateForm(form);
    if (!verdict.ok) {
      formReason = verdict.reason;
      return;
    }
    let expiresAt = null;
    if (form.expiryDay) {
      expiresAt = endOfRomeDay(form.expiryDay);
      if (!expiresAt) {
        formReason = 'expiry';
        return;
      }
    }

    formReason = null;
    submitting = true;
    error = null;
    try {
      const integration = form.integration.trim();
      const created = await apiKeysApi.create(companyId, {
        name: form.name.trim(),
        permissions: form.permissions,
        ...(integration ? { integration } : {}),
        ...(expiresAt ? { expiresAt } : {}),
      });
      createdKey = created.key;
      copyState = '';
      closeForm();
      keys = await apiKeysApi.list(companyId);
    } catch (err) {
      error = err;
    } finally {
      submitting = false;
    }
  }

  async function handleRevoke(key) {
    if (!confirm(tImmediate('companySettings.apiKeys.actions.confirmRevoke', { name: key.name }))) return;
    busyKeyId = key.id;
    error = null;
    try {
      await apiKeysApi.revoke(companyId, key.id);
      keys = await apiKeysApi.list(companyId);
    } catch (err) {
      error = err;
    } finally {
      busyKeyId = '';
    }
  }

  async function copyKey() {
    try {
      await navigator.clipboard.writeText(createdKey);
      copyState = 'copied';
    } catch {
      copyState = 'failed';
    }
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => { if (copyState === 'copied') copyState = ''; }, 3000);
  }

  function dismissSecret() {
    createdKey = '';
    copyState = '';
  }

  function permissionLabel(id) {
    return tImmediate(`companySettings.apiKeys.permissions.${id}.label`, undefined, id);
  }

  onDestroy(() => {
    clearTimeout(copyTimer);
    createdKey = '';
  });
</script>

<div class="card p-6">
  <h2 class="eyebrow eyebrow-lg mb-1 flex items-center gap-2" style="color: var(--color-primary);">
    <KeyRound size={16} />
    {$t('companySettings.apiKeys.title')}
    <InfoHint text={$t('companySettings.apiKeys.titleHint')} />
  </h2>
  <p class="text-xs mb-4 max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.intro')}</p>

  <ErrorBanner {error} />

  {#if createdKey}
    <div class="mb-4 p-4 border-w space-y-3" role="alert" style="border-color: var(--color-secondary);">
      <p class="eyebrow" style="color: var(--color-secondary);">{$t('companySettings.apiKeys.secret.title')}</p>
      <p class="text-xs max-w-3xl" style="color: var(--color-text-body);">{$t('companySettings.apiKeys.secret.warning')}</p>
      <div class="flex gap-2 items-center flex-wrap">
        <label class="sr-only" for="api-key-secret">{$t('companySettings.apiKeys.secret.label')}</label>
        <input id="api-key-secret" readonly value={createdKey} on:focus={(e) => e.currentTarget.select()}
          class="input flex-1 min-w-0 font-mono text-xs" />
        <button type="button" on:click={copyKey}
          class="btn btn-secondary btn-sm gap-2">
          {#if copyState === 'copied'}<Check size={14} /> {$t('companySettings.apiKeys.secret.copied')}{:else}<Copy size={14} /> {$t('companySettings.apiKeys.secret.copy')}{/if}
        </button>
      </div>
      {#if copyState === 'failed'}
        <p class="text-xs" style="color: var(--color-secondary);">{$t('companySettings.apiKeys.secret.copyFailed')}</p>
      {/if}
      <p class="text-xs max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.secret.usage')}</p>
      <button type="button" on:click={dismissSecret}
        class="btn btn-primary btn-sm">
        {$t('companySettings.apiKeys.secret.done')}
      </button>
    </div>
  {/if}

  {#if loading}
    <Spinner label={$t('companySettings.apiKeys.loading')} padding="py-8" />
  {:else}
    {#if keys.length === 0}
      <div class="border-w" style="border-color: var(--color-tint-medium);">
        <EmptyState icon={KeyRound} title={$t('companySettings.apiKeys.emptyTitle')} body={$t('companySettings.apiKeys.emptyBody')} />
      </div>
    {:else}
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b-w" style="border-color: var(--color-tint-medium);">
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.columns.name')}</th>
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.columns.permissions')}</th>
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.columns.status')}</th>
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.columns.expires')}</th>
              <th class="text-left py-3 px-2 eyebrow" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.columns.lastUsed')}</th>
              <th class="text-right py-3 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {#each keys as key (key.id)}
              <tr class="border-b" style="border-color: var(--color-tint-soft);">
                <td class="py-2 px-2 align-top">
                  <span class="font-bold break-all" style="color: var(--color-text-heading);">{key.name}</span>
                  {#if key.integration}
                    <p class="text-xs font-mono mt-1 break-all" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.columns.integration')}: {key.integration}</p>
                  {/if}
                </td>
                <td class="py-2 px-2 align-top">
                  <ul class="text-xs space-y-1" style="color: var(--color-text-body);">
                    {#each key.permissions as id (id)}
                      <li>{permissionLabel(id)}</li>
                    {/each}
                  </ul>
                </td>
                <td class="py-2 px-2 align-top">
                  <span class="px-2 py-0.5 eyebrow"
                    style="color: {key.status === 'active' ? 'var(--color-primary)' : 'var(--color-secondary)'}; background: var(--color-tint-soft);">
                    {$t(`companySettings.apiKeys.status.${key.status}`)}
                  </span>
                </td>
                <td class="py-2 px-2 align-top text-xs" style="color: var(--color-text-muted);">
                  {key.expiresAt ? formatDateTime(key.expiresAt) : $t('companySettings.apiKeys.noExpiry')}
                </td>
                <td class="py-2 px-2 align-top text-xs" style="color: var(--color-text-muted);">
                  {key.lastUsedAt ? formatDateTime(key.lastUsedAt) : $t('companySettings.apiKeys.never')}
                </td>
                <td class="py-2 px-2 text-right align-top whitespace-nowrap">
                  <button type="button" class="p-1 hover:text-action transition-colors" style="color: var(--color-text-muted);"
                    title={$t('companySettings.apiKeys.successor')}
                    aria-label={$t('companySettings.apiKeys.successorLabel', { name: key.name })}
                    on:click={() => openSuccessor(key)}>
                    <RefreshCw size={16} />
                  </button>
                  {#if key.status !== 'revoked'}
                    <button type="button" class="p-1 hover:text-danger transition-colors" style="color: var(--color-text-muted);"
                      disabled={busyKeyId === key.id}
                      title={$t('companySettings.apiKeys.actions.revoke')}
                      aria-label={$t('companySettings.apiKeys.actions.revokeLabel', { name: key.name })}
                      on:click={() => handleRevoke(key)}>
                      <Ban size={16} />
                    </button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    {#if showForm}
      <form on:submit={handleSubmit} class="mt-4 p-4 border-w space-y-4" style="border-color: var(--color-tint-medium);">
        <p class="eyebrow" style="color: var(--color-primary);">
          {successorOfName ? $t('companySettings.apiKeys.successorTitle') : $t('companySettings.apiKeys.createTitle')}
        </p>
        {#if successorOfName}
          <p class="text-xs max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.successorIntro')}</p>
        {/if}

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="api-key-name" class="block eyebrow mb-1" style="color: var(--color-text-muted);">
              {$t('companySettings.apiKeys.fields.name')} *
              <InfoHint text={$t('companySettings.apiKeys.fields.nameHint')} />
            </label>
            <input id="api-key-name" bind:value={form.name} required maxlength="100" placeholder={$t('companySettings.apiKeys.fields.namePlaceholder')}
              class="input" />
          </div>

          <div>
            <label for="api-key-integration" class="block eyebrow mb-1" style="color: var(--color-text-muted);">
              {$t('companySettings.apiKeys.fields.integration')}{integrationRequired ? ' *' : ''}
              <InfoHint text={$t('companySettings.apiKeys.fields.integrationHint')} />
            </label>
            {#if integrationLocked}
              <input id="api-key-integration" readonly value={form.integration}
                class="input opacity-80" />
              <p class="text-xs mt-1" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.fields.integrationLocked')}</p>
            {:else}
              <input id="api-key-integration" bind:value={form.integration} list="api-key-integrations" maxlength="64"
                pattern={INTEGRATION_PATTERN.source} autocomplete="off" required={integrationRequired}
                placeholder={$t('companySettings.apiKeys.fields.integrationPlaceholder')}
                class="input" />
              <datalist id="api-key-integrations">
                {#each knownIntegrations as label (label)}<option value={label}></option>{/each}
              </datalist>
              <p class="text-xs mt-1" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.fields.integrationFormat')}</p>
            {/if}
          </div>
        </div>

        <fieldset>
          <legend class="block eyebrow mb-1" style="color: var(--color-text-muted);">
            {$t('companySettings.apiKeys.fields.permissions')} *
          </legend>
          <p class="text-xs mb-2 max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.apiKeys.fields.permissionsHint')}</p>

          {#if presets.length > 0}
            <div class="mb-3 flex gap-2 flex-wrap items-start">
              {#each presets as preset (preset.id)}
                <button type="button" on:click={() => applyPreset(preset)}
                  class="btn btn-secondary btn-sm gap-2">
                  <Plus size={12} /> {$t(`companySettings.apiKeys.presets.${preset.id}.label`)}
                </button>
                <span class="text-xs max-w-md self-center" style="color: var(--color-text-muted);">{$t(`companySettings.apiKeys.presets.${preset.id}.hint`)}</span>
              {/each}
            </div>
          {/if}

          <div class="space-y-3">
            {#each groups as group (group.group)}
              <div>
                <p class="eyebrow mb-1" style="color: var(--color-primary);">
                  {$t(`companySettings.apiKeys.groups.${group.group}`, undefined, group.group)}
                </p>
                {#each group.ids as id (id)}
                  <label class="flex gap-2 items-start py-1 text-sm cursor-pointer" style="color: var(--color-text-body);">
                    <input type="checkbox" value={id} bind:group={form.permissions} class="mt-1" />
                    <span>
                      <span class="font-bold">{$t(`companySettings.apiKeys.permissions.${id}.label`, undefined, id)}</span>
                      <span class="block text-xs" style="color: var(--color-text-muted);">{$t(`companySettings.apiKeys.permissions.${id}.description`, undefined, '')}</span>
                    </span>
                  </label>
                {/each}
              </div>
            {/each}
          </div>
          {#if wideRead}
            <p class="text-xs mt-2 max-w-3xl" style="color: var(--color-secondary);">{$t('companySettings.apiKeys.wideReadWarning')}</p>
          {/if}
        </fieldset>

        <div class="max-w-xs">
          <label for="api-key-expiry" class="block eyebrow mb-1" style="color: var(--color-text-muted);">
            {$t('companySettings.apiKeys.fields.expiry')}
            <InfoHint text={$t('companySettings.apiKeys.fields.expiryHint')} />
          </label>
          <input id="api-key-expiry" type="date" bind:value={form.expiryDay}
            class="input" />
        </div>

        {#if formReason}
          <p class="text-xs" role="alert" style="color: var(--color-secondary);">{$t(`companySettings.apiKeys.validation.${formReason}`, undefined, '')}</p>
        {/if}

        <div class="flex gap-3">
          <button type="submit" disabled={submitting}
            class="btn btn-primary btn-sm">
            {submitting ? $t('companySettings.apiKeys.actions.submitting') : $t('companySettings.apiKeys.actions.submit')}
          </button>
          <button type="button" on:click={closeForm}
            class="btn btn-secondary btn-sm">
            {$t('common.cancel')}
          </button>
        </div>
      </form>
    {:else}
      <button type="button" on:click={openForm}
        class="btn btn-secondary btn-sm mt-4 gap-2">
        <Plus size={14} /> {$t('companySettings.apiKeys.create')}
      </button>
    {/if}
  {/if}
</div>
